import { createContext, useCallback, useContext } from 'react';
import { AssistantAuditOperationListApi } from './AgentApiProviderTypes';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import {
  AuditOperation,
  TaskScheduleApi,
  TaskScheduleCreateApi,
  TaskScheduleExecutionsApi,
  TaskScheduleListApi,
  TaskScheduleUpdateApi,
} from './TaskScheduleApiProviderTypes';
import { buildUrl } from '../utils/buildQuery';
import { normalizeDate } from '../utils/formatDate';

interface TaskScheduleApiContextType {
  getTaskSchedules: (skip?: number, limit?: number) => Promise<TaskScheduleListApi>;
  getTaskScheduleById: (taskScheduleId: string) => Promise<TaskScheduleApi>;
  createTaskSchedule: (data: TaskScheduleCreateApi) => Promise<TaskScheduleApi>;
  updateTaskSchedule: (
    taskScheduleId: string,
    data: TaskScheduleUpdateApi,
  ) => Promise<TaskScheduleApi>;
  deleteTaskSchedule: (taskScheduleId: string) => Promise<void>;
  getTaskScheduleExecutions: (
    taskScheduleId: string,
    skip?: number,
    limit?: number,
  ) => Promise<TaskScheduleExecutionsApi>;
  getExecutionAuditOperation: (assistantId: string, executionId: string) => Promise<AuditOperation>;
  getExecutionAuditOperations: (
    assistantId: string,
    threadId: string,
    skip?: number,
    limit?: number,
    start_date?: string,
    end_date?: string,
    operationId?: string | null,
  ) => Promise<AssistantAuditOperationListApi>;
  getAssistants: () => Promise<{ id: string; name: string }[]>;
}

const TaskScheduleApiContext = createContext<TaskScheduleApiContextType | null>(null);

export function TaskScheduleApiProvider({ children }: { children: React.ReactNode }) {
  const { get, del, put } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getTaskSchedules = useCallback(
    async (skip: number = 0, limit: number = 10): Promise<TaskScheduleListApi> => {
      try {
        const response = await get(`/v1/task_schedules?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching task schedules:', error);
        throw error;
      }
    },
    [get],
  );

  const updateTaskSchedule = useCallback(
    async (taskScheduleId: string, data: TaskScheduleUpdateApi): Promise<TaskScheduleApi> => {
      try {
        const response = await put(`/v1/task_schedules/${taskScheduleId}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating task schedule:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteTaskSchedule = useCallback(
    async (taskScheduleId: string): Promise<void> => {
      try {
        await del(`/v1/task_schedules/${taskScheduleId}`);
      } catch (error) {
        console.error('Error deleting task schedule:', error);
        throw error;
      }
    },
    [del],
  );

  const createTaskSchedule = useCallback(
    async (data: TaskScheduleCreateApi): Promise<TaskScheduleApi> => {
      try {
        const response = await put(`/v1/task_schedules`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating task schedule:', error);
        throw error;
      }
    },
    [put],
  );

  const getTaskScheduleById = useCallback(
    async (taskScheduleId: string): Promise<TaskScheduleApi> => {
      try {
        const response = await get(`/v1/task_schedules/${taskScheduleId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching task schedule by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getTaskScheduleExecutions = useCallback(
    async (
      taskScheduleId: string,
      skip: number = 0,
      limit: number = 10,
    ): Promise<TaskScheduleExecutionsApi> => {
      try {
        const response = await get(
          `/v1/task_schedules/${taskScheduleId}/executions?skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching task schedule executions:', error);
        throw error;
      }
    },
    [get],
  );

  const getExecutionAuditOperation = useCallback(
    async (assistantId: string, executionId: string): Promise<AuditOperation> => {
      try {
        const response = await get(`/v1/assistants/${assistantId}/audit_operations/${executionId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching task schedule execution by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getExecutionAuditOperations = useCallback(
    async (
      assistantId: string,
      threadId: string,
      skip: number = 0,
      limit: number = 10,
      start_date?: string,
      end_date?: string,
      operationId: string | null = null,
    ): Promise<AssistantAuditOperationListApi> => {
      try {
        const url = buildUrl(`/v1/assistants/${assistantId}/audit_operations`, {
          skip,
          limit,
          start_date: normalizeDate(start_date),
          end_date: normalizeDate(end_date),
          thread_id: threadId,
          operation_id: operationId,
        });
        const response = await get(url);
        return response.data;
      } catch (error) {
        console.error('Error fetching execution audit operations:', error);
        throw error;
      }
    },
    [get],
  );

  const getAssistants = useCallback(async () => {
    try {
      const response = await get(`/v1/assistants`);
      return response.data.items;
    } catch (error) {
      console.error('Error fetching assistants:', error);
      throw error;
    }
  }, [get]);

  return (
    <TaskScheduleApiContext.Provider
      value={{
        getTaskSchedules,
        updateTaskSchedule,
        deleteTaskSchedule,
        createTaskSchedule,
        getTaskScheduleById,
        getTaskScheduleExecutions,
        getExecutionAuditOperation,
        getExecutionAuditOperations,
        getAssistants,
      }}
    >
      {children}
    </TaskScheduleApiContext.Provider>
  );
}

export function useTaskScheduleApi(): TaskScheduleApiContextType {
  const context = useContext(TaskScheduleApiContext);
  if (!context) {
    throw new Error('useTaskScheduleApi must be used within a TaskScheduleApiProvider');
  }
  return context;
}
